import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";

import VLButton from "views/uikit/Buttons/VLButton";
import { VLDropdown } from "views/uikit/VLDropdown/VLDropdown";
import { ScanResultsAvailability } from "hooks/useScanResultsAvailability";
import {
  DatasetSource,
  getScanResultsDisabledReason,
  SOURCE_OPTIONS,
} from "./sources";
import styles from "./ScanImport.module.scss";

interface AddDataMenuProps {
  onOpen: (source: DatasetSource) => void;
  /** null while still being checked. */
  scanResultsAvailability: ScanResultsAvailability | null;
}

/**
 * "Add data" with the sources in a menu, in the folder table's header. A plain menu
 * button rather than a split one: the two sources are peers, with no default to put on
 * the button face.
 *
 * Scan results stay in the menu when DataServer is unavailable, greyed out with the
 * reason in place of its description - visible to keyboard and touch users too, which a
 * tooltip alone would not be.
 */
export default function AddDataMenu({
  onOpen,
  scanResultsAvailability,
}: AddDataMenuProps) {
  const scanResultsDisabledReason = getScanResultsDisabledReason(
    scanResultsAvailability,
  );

  const items: MenuProps["items"] = SOURCE_OPTIONS.map((option) => {
    const disabledReason =
      option.id === "scanResults" ? scanResultsDisabledReason : "";
    return {
      key: option.id,
      disabled: !!disabledReason,
      label: (
        <span className={styles.menuItem}>
          <span className={styles.menuItemTitle}>{option.title}</span>
          <span
            className={
              disabledReason
                ? styles.menuItemReason
                : styles.menuItemDescription
            }
          >
            {disabledReason || option.description}
          </span>
        </span>
      ),
    };
  });

  return (
    <VLDropdown
      trigger={["click"]}
      placement="bottomRight"
      overlayClassName={styles.addDataMenu}
      menu={{
        items,
        onClick: ({ key }) => onOpen(key as DatasetSource),
      }}
    >
      <VLButton
        type="primary"
        size="middle"
        icon={<PlusOutlined />}
        className={styles.addDataButton}
        aria-haspopup="menu"
      >
        Add data <DownOutlined className={styles.addDataCaret} />
      </VLButton>
    </VLDropdown>
  );
}
